import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/format";

export const BRAND_DEFAULTS = {
  colorPrimario: "#FF5400",
  colorAcento: "#FFB800",
  colorFondo: "#0B0B0C",
  fuente: "bebas",
  radio: "clasico",
};

export const FUENTES = {
  bebas: { label: "Bebas Neue (actual)", family: '"Bebas Neue", Impact, sans-serif', google: null },
  barlow: { label: "Barlow Condensed", family: '"Barlow Condensed", sans-serif', google: null },
  archivo: { label: "Archivo Black", family: '"Archivo Black", sans-serif', google: "Archivo+Black" },
  oswald: { label: "Oswald", family: '"Oswald", sans-serif', google: "Oswald:wght@500;600;700" },
};

export const RADIOS = {
  clasico: { label: "Clásico", value: "0.5rem" },
  cuadrado: { label: "Cuadrado", value: "0.125rem" },
  redondeado: { label: "Redondeado", value: "1rem" },
};

const hexToTriplet = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
};

export const lighten = (hex, amt) => {
  const n = parseInt(hex.slice(1), 16);
  const f = (c) => Math.min(255, Math.round(c + (255 - c) * amt)).toString(16).padStart(2, "0");
  return `#${f((n >> 16) & 255)}${f((n >> 8) & 255)}${f(n & 255)}`.toUpperCase();
};

export function brandingVars(b) {
  const fondo = b.colorFondo || BRAND_DEFAULTS.colorFondo;
  const primario = b.colorPrimario || BRAND_DEFAULTS.colorPrimario;
  const acento = b.colorAcento || BRAND_DEFAULTS.colorAcento;
  return {
    "--c-brand": hexToTriplet(primario),
    "--c-brand-strong": hexToTriplet(lighten(primario, 0.08)),
    "--c-gold": hexToTriplet(acento),
    "--c-gold-strong": hexToTriplet(lighten(acento, 0.12)),
    "--c-ink": hexToTriplet(fondo),
    "--c-panel": hexToTriplet(lighten(fondo, 0.035)),
    "--c-line": hexToTriplet(lighten(fondo, 0.12)),
    "--font-heading": (FUENTES[b.fuente] || FUENTES.bebas).family,
    "--radius": (RADIOS[b.radio] || RADIOS.clasico).value,
  };
}

function loadFont(fuente) {
  const f = FUENTES[fuente];
  const id = "brand-font-link";
  let link = document.getElementById(id);
  if (!f || !f.google) { link && link.remove(); return; }
  if (!link) {
    link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  link.href = `https://fonts.googleapis.com/css2?family=${f.google}&display=swap`;
}

export function applyBranding(branding, target) {
  const merged = { ...BRAND_DEFAULTS, ...(branding || {}) };
  const vars = brandingVars(merged);
  const el = target || document.documentElement;
  Object.entries(vars).forEach(([k, v]) => el.style.setProperty(k, v));
  if (!target) loadFont(merged.fuente);
  return vars;
}

function setBrandIcons(logoUrl) {
  ["icon", "apple-touch-icon"].forEach((rel) => {
    let link = document.querySelector(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement("link");
      link.rel = rel;
      document.head.appendChild(link);
    }
    link.href = logoUrl;
  });
}

function setMeta(name, content, attr = "property") {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function applyOgTags(cfg) {
  const titulo = cfg.ogTitulo || cfg.tituloPestana || cfg.nombreLocal;
  if (titulo) setMeta("og:title", titulo);
  const desc = cfg.ogDescripcion || "";
  if (desc) {
    setMeta("og:description", desc);
    setMeta("description", desc, "name");
  }
  const img = cfg.ogImagen || cfg.logoUrl;
  if (img) {
    const abs = img.startsWith("/") ? `${window.location.origin}${img}` : img;
    setMeta("og:image", abs);
  }
  setMeta("og:site_name", cfg.nombreLocal || "");
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/config`)
      .then((r) => {
        setCfg(r.data);
        applyBranding(r.data?.branding);
        const titulo = r.data?.tituloPestana || r.data?.nombreLocal;
        if (titulo) document.title = titulo;
        if (r.data?.logoUrl) setBrandIcons(r.data.logoUrl);
        if (r.data) applyOgTags(r.data);
      })
      .catch(() => {});
  }, []);

  const branding = { ...BRAND_DEFAULTS, ...(cfg?.branding || {}) };
  return <ThemeContext.Provider value={{ cfg, branding }}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
